import { useCallback } from 'react';
import classNames from './ChangeLevelPopup.module.scss';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';
import { StudentReportItem } from '@/school-management/types/response';
import {
  BulkOperationResponseData,
  ChangeStudentsLevelBulkOperationItem,
  BulkOperationState,
} from '@/school-management/types/services/bulk';
import AppIcon from '@/components/school-management/AppIcon/AppIcon';
import processing from '@/school-management/utils/processing';

interface DownloadProps {
  students: StudentReportItem[];
  newLevelLabel: string;
  bulkOperation?: BulkOperationResponseData;
  bulkOperationState:
    | BulkOperationState.Completed
    | BulkOperationState.PartialSuccess
    | BulkOperationState.Failed;
}

const Download = ({
  students,
  newLevelLabel,
  bulkOperation,
  bulkOperationState,
}: DownloadProps) => {
  const { t } = useAdminTranslation();

  const download = useCallback(() => {
    const studentsToProcess = students || [];

    const bulkStudentsMapper = (
      (bulkOperation?.results || []) as ChangeStudentsLevelBulkOperationItem[]
    ).reduce(
      (acc, s) => {
        if (s.studentId) {
          acc[s.studentId] = s;
        }

        return acc;
      },
      {} as {
        [studentId: string]: ChangeStudentsLevelBulkOperationItem;
      }
    );

    const tableName = t('bulkActions.changeLevel.report.fileName');
    const keys = {
      name: t('bulkActions.common.name'),
      class: t('bulkActions.changeLevel.report.class'),
      previousLevel: t('bulkActions.changeLevel.report.previousLevel'),
      newLevel: t('bulkActions.changeLevel.report.newLevel'),
      action: t('bulkActions.common.action'),
      status: t('status'),
    };
    const headerRow: string[] = [
      keys.name,
      keys.class,
      keys.previousLevel,
      keys.newLevel,
      keys.action,
      keys.status,
    ];
    const data: {
      [key: string]: any;
    }[] = studentsToProcess.map(s => {
      const isSuccess =
        bulkOperationState === BulkOperationState.Completed
          ? true
          : bulkOperationState === BulkOperationState.Failed
            ? false
            : s.userId in bulkStudentsMapper
              ? bulkStudentsMapper[s.userId].success
              : true;

      return {
        [keys.name]: s.name,
        [keys.class]: s.groupName,
        [keys.previousLevel]: s.currentLevel ? t(`levelName_${s.currentLevel}`) : '',
        [keys.newLevel]: newLevelLabel,
        [keys.action]: t('bulkActions.changeLevel.report.actionChangeLevel'),
        [keys.status]: isSuccess ? 'success' : 'failure',
      };
    });

    processing.createXlsx({
      fileName: tableName,
      tables: [
        {
          name: tableName,
          headerRow: headerRow,
          data: data,
        },
      ],
    });
  }, [students, newLevelLabel, bulkOperation, bulkOperationState, t]);

  return (
    <div className={classNames.download}>
      <button onClick={download}>
        <AppIcon icon='download' width={10} height={15} />
        <span>{t('bulkActions.downloadChanges.downloadButton')}</span>
      </button>
      <div>{t('bulkActions.downloadChanges.text')}</div>
    </div>
  );
};

export default Download;
